(function () {
  const REGISTERED_KEY = "__dapSummaryPillRegistered";

  function formatPostcode(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 5) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return String(raw);
  }

  function getLocale() {
    const lang = document.documentElement.getAttribute("lang");
    if (!lang) return "sv-SE";
    return lang.replace("_", "-");
  }

  function formatDate(isoDate, locale) {
    if (!isoDate) return "";
    const [y, m, d] = String(isoDate).split("-").map(Number);
    if (!y || !m || !d) return String(isoDate);
    const date = new Date(Date.UTC(y, m - 1, d));
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      }).format(date);
    } catch {
      return String(isoDate);
    }
  }

  function renderTemplate(template, vars) {
    if (!template) return "";
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => vars[key] ?? "");
  }

  class DapSummaryPill extends HTMLElement {
    #unsub = null;
    #onSessionChangeEvt = null;
    #observer = null;
    #syncing = false;

    connectedCallback() {
      this.#sync();
      this.#subscribe();
      this.#onSessionChangeEvt = () => this.#sync();
      window.addEventListener("dap:session-change", this.#onSessionChangeEvt);
      // Re-sync once at next frame in case session/DAP became ready after upgrade
      requestAnimationFrame(() => {
        if (this.isConnected) this.#sync();
      });
      // Re-sync if external DOM morph (Shopify section hydration) wipes our state
      this.#observer = new MutationObserver(() => {
        if (this.#syncing) return;
        this.#sync();
      });
      this.#observer.observe(this, {
        attributes: true,
        attributeFilter: ["hidden"],
        childList: true,
      });
    }

    disconnectedCallback() {
      this.#unsub?.();
      this.#unsub = null;
      if (this.#onSessionChangeEvt) {
        window.removeEventListener(
          "dap:session-change",
          this.#onSessionChangeEvt,
        );
        this.#onSessionChangeEvt = null;
      }
      this.#observer?.disconnect();
      this.#observer = null;
    }

    #subscribe() {
      if (this.#unsub) return;
      if (window.DAP?.onSessionChange) {
        this.#unsub = window.DAP.onSessionChange(() => this.#sync());
        return;
      }
      // Retry once DAP loads
      requestAnimationFrame(() => {
        if (this.isConnected) this.#subscribe();
      });
    }

    #sync() {
      if (this.#syncing) return;
      this.#syncing = true;
      try {
        this.#syncImpl();
      } finally {
        // Drain any mutation records caused by our own write so the observer
        // does not re-trigger #sync recursively.
        this.#observer?.takeRecords();
        this.#syncing = false;
      }
    }

    #syncImpl() {
      const valid = window.DAP?.isSessionValid?.();
      const session = valid ? window.DAP?.getSession?.() : null;

      if (!session?.date || !session?.postcode) {
        this.textContent = "";
        this.hidden = true;
        return;
      }

      const locale = getLocale();
      const date = formatDate(session.date, locale);
      const postcode = formatPostcode(session.postcode);
      const address = session.address || "";
      const city = session.city || "";
      const fullAddress = [
        address,
        [postcode, city].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ");
      const context = this.dataset.context || "global";

      const labelTemplate =
        context === "plp" && this.dataset.plpTemplate
          ? this.dataset.plpTemplate
          : this.dataset.pillTemplate || "{{ date }} · {{ full_address }}";

      const label = renderTemplate(labelTemplate, {
        date,
        postcode,
        address,
        city,
        full_address: fullAddress,
      });
      const changeLabel = this.dataset.changeLabel || "Ändra";
      const dialogId = this.dataset.dialogId || "delivery-dialog";

      this.replaceChildren();
      this.dataset.state = "filled";
      this.hidden = false;

      const text = document.createElement("span");
      text.className = "dap-summary-pill__text";
      text.textContent = label;
      this.appendChild(text);

      const change = document.createElement("button");
      change.type = "button";
      change.className = "dap-summary-pill__change";
      change.setAttribute("aria-haspopup", "dialog");
      change.dataset.dapTrigger = "";
      change.textContent = changeLabel;
      change.addEventListener("click", (e) => {
        e.preventDefault();
        for (const section of document.querySelectorAll(
          "[data-dap-section][data-dap-resolved]",
        )) {
          section.removeAttribute("data-dap-resolved");
        }
        const dialog = document.getElementById(dialogId);
        if (dialog && typeof dialog.showDialog === "function") {
          dialog.showDialog();
        }
      });
      this.appendChild(change);

      const resetLabel = this.dataset.resetLabel || "Rensa";
      const reset = document.createElement("button");
      reset.type = "button";
      reset.className = "dap-summary-pill__reset";
      reset.textContent = resetLabel;
      reset.addEventListener("click", (e) => {
        e.preventDefault();
        for (const section of document.querySelectorAll(
          "[data-dap-section][data-dap-resolved]",
        )) {
          section.removeAttribute("data-dap-resolved");
        }
        const picker = document.querySelector("delivery-picker");
        picker?.dispatchEvent(
          new CustomEvent("dap:request-reset", { bubbles: false }),
        );
      });
      this.appendChild(reset);
    }
  }

  if (!globalThis[REGISTERED_KEY]) {
    if (!customElements.get("dap-summary-pill")) {
      customElements.define("dap-summary-pill", DapSummaryPill);
    }
    globalThis[REGISTERED_KEY] = true;
  }
})();
